import {
  PAY_TYPE_OPTIONS,
  useGetSalaryGradeQuery,
  useGetSalaryStructureQuery,
  type PayType,
} from '@/app/features/employee/emp-payroll';
import ModuleGuard from '@/components/Guards/ModuleGuard';
import MODULES from '@/config/modules.config';
import { ChevronDownIcon } from '@/icons';
import { validatePositiveNumber } from '@/utils';
import { snakeToCamel } from '@/utils/string.utils';
import { Flex, Form, InputNumber, Select } from 'antd';
import { useEffect } from 'react';

/**
 * Pay Type + Grade + Salary + structure breakdown.
 *
 * Shared by Create Employee and Update Employee → Job Information, which used
 * to carry two drifting copies of this block.
 *
 * Renders four grid cells, so drop it inside a 2-column grid:
 *   Pay Type | Grade
 *   Salary   | Breakdown
 */
const PayTypeGradeSalaryFields = () => {
  const form = Form.useFormInstance();

  const payType: PayType = Form.useWatch('payBasis', form) || 'MONTHLY';
  const gradeId = Form.useWatch('grade', form);
  const isDaily = payType === 'DAILY';

  // Grades and structure are pay-type scoped: a daily employee can only be put
  // on a daily grade, and the breakdown shown is the daily structure.
  const { data: salaryGradeData, isLoading: isGradeLoading } =
    useGetSalaryGradeQuery(payType);
  const grades = salaryGradeData?.data?.grades || [];
  const selectedGrade = grades.find((g: any) => g.id === gradeId);

  const { data: salaryStructureData } = useGetSalaryStructureQuery(payType);
  const structures = salaryStructureData?.data?.salary_structures || [];

  const dailyRate = Number(selectedGrade?.daily_rate_bdt || 0);

  useEffect(() => {
    if (!isDaily) {
      form.setFieldValue('dailyRateBdt', undefined);
      return;
    }
    // The day rate is not typed in — it comes from the selected daily grade.
    form.setFieldValue('dailyRateBdt', dailyRate || undefined);
    // `salary` stays populated so the existing required-salary contract on the
    // employee endpoint keeps being satisfied for daily employees too.
    form.setFieldValue('salary', dailyRate || undefined);
  }, [isDaily, dailyRate, form]);

  const handlePayTypeChange = () => {
    form.setFieldsValue({
      grade: undefined,
      salary: undefined,
      dailyRateBdt: undefined,
    });
  };

  return (
    <>
      {/* Pay Type */}
      <ModuleGuard moduleId={MODULES.EMPLOYEE_PAYROLL}>
        <Form.Item
          label="Pay Type"
          name="payBasis"
          initialValue="MONTHLY"
          rules={[{ required: true, message: 'Please select pay type' }]}
        >
          <Select
            size="large"
            className="w-full"
            placeholder="Select Pay Type"
            options={PAY_TYPE_OPTIONS}
            onChange={handlePayTypeChange}
            suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
          />
        </Form.Item>
      </ModuleGuard>

      {/* Grade */}
      <ModuleGuard moduleId={MODULES.EMPLOYEE_PAYROLL}>
        <Form.Item
          label="Grade"
          name="grade"
          rules={[{ required: true, message: 'Please select grade' }]}
        >
          <Select
            size="large"
            className="w-full"
            placeholder={isDaily ? 'Select Daily Grade' : 'Select Grade'}
            allowClear
            showSearch
            optionFilterProp="label"
            loading={isGradeLoading}
            notFoundContent={
              isDaily ? 'No daily grade configured yet' : undefined
            }
            options={grades?.map((grade: any) => ({
              label: snakeToCamel(grade?.grade_name),
              value: grade?.id,
            }))}
            suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
          />
        </Form.Item>
      </ModuleGuard>

      {/* Salary (monthly) or Daily Rate (daily) */}
      <ModuleGuard
        moduleId={MODULES.EMPLOYEE_PAYROLL}
        fallback={
          <Form.Item
            label="Gross Salary"
            name="salary"
            validateFirst
            rules={[
              { required: true, message: 'Please enter salary' },
              {
                validator: (_, value) =>
                  value === 0
                    ? Promise.reject(new Error('Salary cannot be zero'))
                    : Promise.resolve(),
              },
              { validator: validatePositiveNumber },
            ]}
          >
            <InputNumber
              min={1}
              size="large"
              type="number"
              controls={false}
              placeholder="Salary"
              className="w-full"
            />
          </Form.Item>
        }
      >
        {isDaily ? (
          <div>
            <Form.Item label="Daily Rate" name="dailyRateBdt">
              <InputNumber
                disabled
                size="large"
                controls={false}
                placeholder="Select a grade first"
                className="w-full"
              />
            </Form.Item>
            <div className="text-xs text-white mt-1">
              [ <span className="text-[#FF255C]">Note: </span>
              {selectedGrade
                ? `Day rate comes from the selected grade: BDT ${dailyRate}`
                : 'Select a daily grade to load its fixed day rate'}]
            </div>
          </div>
        ) : (
          <div>
            <Form.Item
              label="Gross Salary"
              name="salary"
              validateFirst
              rules={[
                { required: !!selectedGrade, message: 'Please enter salary' },
                {
                  validator: (_, value) => {
                    if (value === 0) {
                      return Promise.reject(new Error('Salary cannot be zero'));
                    }
                    if (value < (selectedGrade?.min_salary_bdt || 0)) {
                      return Promise.reject(
                        new Error('Salary cannot be less than minimum salary'),
                      );
                    }
                    if (value > (selectedGrade?.max_salary_bdt || 0)) {
                      return Promise.reject(
                        new Error('Salary cannot be greater than maximum salary'),
                      );
                    }
                    return Promise.resolve();
                  },
                },
                { validator: validatePositiveNumber },
              ]}
            >
              <InputNumber
                disabled={!selectedGrade}
                min={selectedGrade?.min_salary_bdt}
                max={selectedGrade?.max_salary_bdt}
                controls={false}
                size="large"
                type="number"
                placeholder="Salary"
                className="w-full"
              />
            </Form.Item>
            {selectedGrade && (
              <div className="text-xs text-white mt-1">
                [ <span className="text-[#FF255C]">Note: </span>Salary range is
                BDT {selectedGrade?.min_salary_bdt || 0} - BDT{' '}
                {selectedGrade?.max_salary_bdt || 0}]
              </div>
            )}
          </div>
        )}
      </ModuleGuard>

      {/* Structure breakdown */}
      <ModuleGuard moduleId={MODULES.EMPLOYEE_PAYROLL}>
        <div>
          <Flex
            align="center"
            justify="space-between"
            gap={40}
            className="mb-4 pb-4 border-b border-theme-slate-700"
          >
            <span className="text-white text-base">
              {isDaily ? 'Daily Breakdown' : 'Total Breakdown'}
            </span>
            <span className="text-white text-base">
              {structures?.reduce(
                (a: number, s: any) => a + Number(s.component_percentage),
                0,
              ) || 0}
              %
            </span>
          </Flex>
          {structures?.map((s: any) => (
            <Flex key={s.id} align="center" justify="space-between" gap={40}>
              <span className="px-5 py-2 mb-2 last:mb-0 text-white border border-theme-slate-700 rounded-lg w-full max-w-[200px]">
                {snakeToCamel(s.component_name)}
              </span>
              <span className="text-white text-base">
                {s.component_percentage}%
              </span>
            </Flex>
          ))}
        </div>
      </ModuleGuard>
    </>
  );
};

export default PayTypeGradeSalaryFields;
